import { useId } from '@fluentui/react-hooks';
import { ITooltipHostStyles, TooltipHost } from '@fluentui/react/lib/Tooltip';
import * as React from 'react';

import { LiveSiteEventSeverity, LiveSiteEventState } from '../../models/status';
import { SvgProps } from '../../models/ui/svg';
import { getSeverityText } from '../../utilities/status';
import { isNil } from '../../utilities/typeGuards';
import { StatusIcon } from './StatusIcon';

const calloutProps = { gapSpace: 0 };
// The TooltipHost root uses display: inline by default.
// If that's causing sizing issues or tooltip positioning issues, try overriding to inline-block.
const hostStyles: Partial<ITooltipHostStyles> = { root: { display: 'inline-block' } };
interface StatusIndicatorProps {
    /**
     * Should the text be listed inline or as a tooltip.  Defaults to tooltip.
     */
    inline?: boolean;

    /**
     * Should the color of the icon be inverted from the standard.  Only applies to Healthy at this point in time.
     */
    invert?: boolean;

    /**
     * The region to include in any messaging.
     */
    region?: string;

    /**
     * The severity of the incident.
     */
    severity: LiveSiteEventSeverity | undefined;

    /**
     *
     */
    state: LiveSiteEventState | undefined;

    /**
     * Extra styling that will be applied on the containing element
     */
    style?: React.CSSProperties;

    /**
     * Override aspects of the SVG displayed if necessary
     */
    svgStyles?: SvgProps;
}

/**
 * Show the status icon + tooltip or inline text describing the icon
 */
export const StatusIndicator = (props: StatusIndicatorProps) => {
    // Use useId() to ensure that the ID is unique on the page.
    // (It's also okay to use a plain string and manually ensure uniqueness.)
    const inline = isNil(props.inline) ? false : props.inline;
    const containerStyle: React.CSSProperties = {
        ...props.style
    };
    const tooltipText = getSeverityText(props.region, props.state, props.severity);

    if (!inline) {
        const tooltipId = useId('tooltip');

        return (
            <TooltipHost
                content={tooltipText}
                // This id is used on the tooltip itself, not the host
                // (so an element with this id only exists when the tooltip is shown)
                id={tooltipId}
                calloutProps={calloutProps}
                styles={hostStyles}
            >
                <div
                    aria-describedby={tooltipId}
                    aria-label={tooltipText}
                    aria-labelledby={tooltipId}
                    role="img"
                    style={containerStyle}>
                    <StatusIcon {...props} {...props.svgStyles} />
                </div>
            </TooltipHost>
        );
    }

    return (
        <div style={containerStyle}>
            <StatusIcon {...props} {...props.svgStyles} />{tooltipText}
        </div>
    );
};